// One row of the composer's sortable item list (Figma Composer item 4257:2140).
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Copy, GripVertical, ImageOff, Link as LinkIcon, Loader2, Pencil, PlayCircle, Undo2, Unlink, Video, X } from "lucide-react";
import { useEffect, useRef, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { iconFor, isVideoItem, kindIcon, kindOf, PENDING_LINK_ICON, type PlayerKind } from "../../player";
import { contentErrorText, wrapsErrorText } from "./contentErrorText";
import { formatDuration } from "./formatDuration";
import { toMb } from "../../utils/chatMedia";
import { codePointCount } from "./codePoints";
import { titleTooLong, type ComposerItem } from "./composerModel";
import Img from "../ui/Img";
import { shownImage, useExternalMediaAllowed } from "../../lib/externalMedia";

type IconType = ComponentType<{ size?: number; className?: string; "aria-hidden"?: boolean }>;

/** The row's kind: a file's, a resolved link's; null for a link not resolved yet. */
export function itemKind(item: ComposerItem): PlayerKind | null {
  if (item.kind === "file") return kindOf(item);
  return item.preview ? kindOf(item.preview) : null;
}

export interface ComposerItemRowProps {
  item: ComposerItem;
  index: number;
  total: number;
  onRemove(): void;
  onUrlChange(url: string): void;
  onTitleChange(t: string): void;
  onTitleReset(): void;
  /** A failed new video, within its retry window: try the upload again. */
  onRetry?(): void;
  /** Keyboard / screen-reader reordering (Move up / Move down); the button is absent at the ends. */
  onMoveUp?(): void;
  onMoveDown?(): void;
  autoFocusUrl?: boolean;
}

interface ViewProps extends ComposerItemRowProps {
  /** Lifted (the DragOverlay copy, or the row being dragged): primary border and grip. */
  lifted?: boolean;
  /** Display-only: no sortable wiring. */
  display?: boolean;
  handleProps?: Record<string, unknown>;
}

/** Presentational row; the sortable wiring lives in the default export, the DragOverlay uses `display`. */
export function ComposerItemRowView(props: ViewProps) {
  const externalOk = useExternalMediaAllowed();
  const { item, index, total, onRemove, onUrlChange, onTitleChange, onTitleReset, onRetry, onMoveUp, onMoveDown, autoFocusUrl, lifted, display, handleProps } = props;
  const { t } = useTranslation();
  const titleRef = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);
  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // clipboard not available: silent, like the wall post card
    }
  };

  const kind = itemKind(item);
  const Icon: IconType = kind ? kindIcon(kind) : iconFor(PENDING_LINK_ICON);
  const typeLabel = kind?.typeLabel(t) ?? "";
  const fileErrorFallback: Record<string, string> = {
    wall_photo_bad_type: "Only JPEG, PNG or WebP", wall_photo_too_large: "Photo is larger than 15 MB", image_svg_too_large: "This SVG is over 1 MB", wall_photo_upload_failed: "Upload failed",
    wall_editor_upload_failed_retry: "Upload failed · tap to retry", video_too_large_browser: "Video is too large to send from this browser",
    video_too_large: "Video is too large to send (over 1 GB)", video_type_not_supported: "This video type can't be sent (use MP4, MOV or WebM)",
  };

  const link = item.kind === "link" ? item : null;
  const file = item.kind === "file" ? item : null;
  const name = file
    ? (file.name ?? (isVideoItem(file) ? t("wall_video", "Video") : t("wall_item_photo", "Photo")))
    : (link!.title || link!.preview?.title || link!.url);
  // The URL stays an input until it resolves: "checking" starts on the first keystroke (debounced
  // resolve), and swapping the input for text then would drop focus mid-typing.
  const showInput = link != null && link.status !== "ok";
  const resolving = link?.status === "checking";
  const uploading = file?.status === "uploading";
  const failed = file?.status === "error" || link?.status === "error";
  // File errors are full i18n keys (composerModel); link errors are bare server codes (wall_error_<code>).
  const errText = failed
    ? file ? t(file.error ?? "wall_photo_upload_failed", fileErrorFallback[file.error ?? "wall_photo_upload_failed"] ?? "Upload failed")
           : link!.error ? contentErrorText(t, link!.error, link!.error === "ITEM_NOT_ALLOWED" ? "This item can't be posted on this wall" : link!.error) : null
    : null;
  // A link's thumbnail comes from the other site: only after external media is allowed (a file's preview is ours).
  const thumb = file ? file.previewUrl : shownImage(link?.preview?.thumbnailUrl, externalOk);
  const dur = formatDuration(item.durationMs ?? null);
  const FailIcon = file ? ImageOff : Unlink;
  // A new video sending (Figma Composer item Type=Video, State=Compressing / Uploading): progress line and bar, × cancels.
  const progress = uploading ? file?.progress : undefined;
  const progressText = progress
    ? progress.phase === "uploading"
      ? t("wall_editor_uploading", { percent: progress.percent, sent: toMb(progress.sent), total: toMb(progress.total), defaultValue: "Uploading {{percent}}% · {{sent}} / {{total}} MB" })
      : t("wall_editor_compressing", { percent: progress.percent, defaultValue: "Compressing {{percent}}%" })
    : null;
  const canRetry = failed && file?.retryable === true && onRetry != null && !display;

  // An ok link carries an editable title (#26): Default (from the link) / Edited / Too long (typed, over 120).
  const titleField = link != null && link.status === "ok" && !failed;
  const tooLong = titleField && titleTooLong(link!);
  const edited = titleField && link!.titleEdited;
  const canUndo = edited && !tooLong && link!.linkTitle != null;
  const titleBorder = tooLong ? "border-[var(--destructive)]" : edited ? "border-[var(--primary-text)]" : "border-[var(--border-strong)]";
  const sourceText = edited ? t("wall_link_title_yours", "your title") : t("wall_link_title_from_link", "title from the link");
  const titleLen = titleField ? codePointCount(link!.title.trim()) : 0;

  const border = lifted ? "border-[var(--primary-text)] shadow-lg" : failed || tooLong ? "border-[var(--destructive)]" : "border-[var(--border)]";

  return (
    <div data-testid={`composer-item-${item.key}`} className={`flex items-center gap-2 rounded-xl border bg-[var(--bg-surface)] p-2 ${border}`}>
      <button
        type="button"
        {...handleProps}
        aria-label={t("wall_reorder_handle", { defaultValue: "Reorder {{name}}", name })}
        className={`touch-none select-none ${display ? "" : "cursor-grab"} ${lifted ? "text-[var(--primary)] dark:text-[var(--primary-on-surface)]" : "text-[var(--foreground-subtle)]"}`}
      ><GripVertical size={16} aria-hidden /></button>
      <div className={`relative flex h-9 w-16 shrink-0 items-center justify-center overflow-hidden rounded ${failed ? "bg-[color-mix(in_srgb,var(--destructive)_12%,transparent)] text-[var(--destructive)]" : "bg-[var(--bg-elevated)] text-[var(--foreground-muted)]"}`}>
        {failed ? <FailIcon size={18} aria-hidden />
          : thumb ? <Img src={thumb} alt="" className={`h-full w-full object-cover ${uploading ? (progress ? "opacity-45" : "opacity-50") : ""}`} />
          : resolving ? null : <Icon size={18} aria-hidden />}
        {(resolving || uploading) && <Loader2 size={18} className="absolute animate-spin text-[var(--foreground-muted)]" aria-hidden />}
        {!failed && !uploading && file != null && isVideoItem(file) && thumb && <PlayCircle size={20} className="absolute text-white drop-shadow" aria-hidden />}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {showInput ? (
          <input
            type="url"
            value={link.url}
            autoFocus={autoFocusUrl}
            placeholder={t("wall_link_placeholder", "Paste a YouTube, SoundCloud, audio or radio link")}
            aria-label={t("wall_link_placeholder", "Paste a YouTube, SoundCloud, audio or radio link")}
            onChange={(e) => onUrlChange(e.target.value)}
            className="w-full bg-transparent text-sm text-[var(--foreground)] outline-none placeholder:text-[var(--foreground-subtle)]"
          />
        ) : titleField ? (
          <div className={`flex w-full items-center gap-1 rounded border bg-[var(--bg-elevated)] py-1 pl-2 pr-1.5 ${titleBorder}`}>
            <input
              ref={titleRef}
              type="text"
              value={link!.title}
              readOnly={display}
              aria-label={t("wall_link_title_label", "Item title")}
              aria-invalid={tooLong || undefined}
              onChange={(e) => onTitleChange(e.target.value)}
              className="min-w-0 flex-1 truncate bg-transparent text-[13px] font-medium text-[var(--foreground)] outline-none"
            />
            {canUndo ? (
              <button type="button" onClick={display ? undefined : onTitleReset}
                aria-label={t("wall_link_title_reset", "Use the title from the link")} className="text-[var(--primary)] dark:text-[var(--primary-on-surface)]">
                <Undo2 size={16} aria-hidden />
              </button>
            ) : (
              <button type="button" tabIndex={-1} onClick={() => titleRef.current?.focus()}
                aria-label={t("wall_link_title_edit", "Edit title")} className="text-[var(--foreground-subtle)]">
                <Pencil size={16} aria-hidden />
              </button>
            )}
          </div>
        ) : (
          <span className="truncate text-[13px] font-medium text-[var(--foreground)]">{name}</span>
        )}
        {titleField && (
          // Read-only URL (Figma "Url row"): the pasted link stays visible and copyable, never editable.
          <div className="flex w-full items-center gap-1">
            <LinkIcon size={14} className="shrink-0 text-[var(--foreground-subtle)]" aria-hidden />
            <span data-testid="composer-link-url" className="min-w-0 flex-1 select-text truncate text-xs text-[var(--foreground-muted)]">{link!.url}</span>
            <button type="button" onClick={display ? undefined : () => void copyUrl(link!.url)}
              aria-label={t("wall_copy_link", "Copy link")} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[var(--foreground-muted)]">
              <Copy size={16} aria-hidden />
            </button>
            {copied && <span role="status" className="shrink-0 text-xs text-[var(--foreground)]">{t("wall_link_copied", "Link copied")}</span>}
          </div>
        )}
        {failed && errText && canRetry ? (
          // Figma State=Error: "Upload failed · tap to retry" (the 10-minute retry window is still open).
          <button type="button" role="alert" data-testid={`composer-item-retry-${item.key}`} onClick={onRetry}
            className="flex items-center gap-1 text-left text-xs text-[var(--destructive)]">
            <Video size={12} aria-hidden /><span className="truncate">{errText}</span>
          </button>
        ) : failed && errText ? (
          <span role="alert" className={`flex gap-1 text-xs text-[var(--destructive)] ${wrapsErrorText(link?.error) ? "items-start" : "items-center"}`}>
            {file != null && isVideoItem(file) ? <Video size={12} aria-hidden /> : <FailIcon size={12} aria-hidden className="shrink-0" />}
            {/* Preview only (Figma 4354:8004): the meta wraps to two lines. */}
            <span className={wrapsErrorText(link?.error) ? "break-words" : "truncate"}>{errText}</span>
          </span>
        ) : progressText ? (
          <>
            <span className="flex items-center gap-1 text-xs text-[var(--foreground-muted)]">
              <Video size={12} aria-hidden /><span className="truncate">{progressText}</span>
            </span>
            <div role="progressbar" aria-label={progressText} aria-valuenow={progress!.percent} aria-valuemin={0} aria-valuemax={100}
              data-testid={`editor-upload-progress-${item.key}`} className="relative h-[3px] w-full bg-[var(--bg-hover)]">
              <div className="absolute inset-y-0 left-0 bg-[var(--primary)]" style={{ width: `${progress!.percent}%` }} />
            </div>
          </>
        ) : resolving ? (
          <span className="text-xs text-[var(--foreground-muted)]">{t("wall_link_resolving", "Resolving link…")}</span>
        ) : uploading ? (
          <span className="text-xs text-[var(--foreground-muted)]">{t("wall_item_uploading", "Uploading…")}</span>
        ) : (link == null || link.preview) ? (
          <span className={`flex items-center gap-1 text-xs ${tooLong ? "text-[var(--destructive)]" : "text-[var(--foreground-muted)]"}`}>
            <Icon size={12} aria-hidden />
            {tooLong
              ? <span role="alert">{t("wall_link_title_too_long", { defaultValue: "Title too long · {{count}} / 120", count: titleLen })}</span>
              : <>{typeLabel}{dur ? ` · ${dur}` : ""}{titleField ? ` · ${sourceText}` : ""}</>}
          </span>
        ) : null}
      </div>
      {!display && onMoveUp && index > 0 && (
        <button type="button" onClick={onMoveUp} className="sr-only focus:not-sr-only text-xs text-[var(--primary)] dark:text-[var(--primary-on-surface)]">
          {t("wall_move_up", { defaultValue: "Move {{name}} up", name })}
        </button>
      )}
      {!display && onMoveDown && index < total - 1 && (
        <button type="button" onClick={onMoveDown} className="sr-only focus:not-sr-only text-xs text-[var(--primary)] dark:text-[var(--primary-on-surface)]">
          {t("wall_move_down", { defaultValue: "Move {{name}} down", name })}
        </button>
      )}
      <button
        type="button"
        onClick={display ? undefined : onRemove}
        aria-label={progress ? t("wall_editor_cancel_upload", "Cancel upload") : t("wall_remove_item", { defaultValue: "Remove {{title}}", title: name })}
        className="text-[var(--foreground-muted)]"
      ><X size={16} aria-hidden /></button>
    </div>
  );
}

export default function ComposerItemRow(props: ComposerItemRowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: props.item.key });
  // Other rows never shift (the list uses a no-op strategy); only the origin slot becomes the dashed placeholder.
  const style = { transform: CSS.Transform.toString(transform), transition };
  if (isDragging) {
    return (
      <div ref={setNodeRef} style={style} data-testid={`composer-slot-${props.item.key}`} aria-hidden
        className="h-[54px] rounded-xl border border-dashed border-[var(--border-strong)] bg-[var(--bg-elevated)]" />
    );
  }
  return (
    <div ref={setNodeRef} style={style}>
      <ComposerItemRowView {...props} handleProps={{ ref: setActivatorNodeRef, ...attributes, ...listeners }} />
    </div>
  );
}
